import { memo, useCallback } from "react";
import { useFormField } from "../../../hooks/useFormField";
import type { FieldRenderInputProps, FieldValue } from "../../../types";
import {
  extractValueFromValueLabel,
  getSelectedOptionLabel,
} from "../../../utils/value-label-utils";
import type { BaseFieldProps } from "../BaseField";
import { BaseField } from "../BaseField";
import { DynamicChangeSelectFieldPresentation } from "./DynamicChangeSelectField.presenter";
import { useDynamicSelectField } from "./useDynamicSelectField";
import type { FieldCode } from "./useFieldDependencyResolve";
import { usePreviousValueLabel } from "./usePreviousValueLabel";

interface DynamicChangeSingleSelectFieldContainerProps extends BaseFieldProps {
  /** 依存元フィールドのコード */
  sourceFieldCode: FieldCode;
  /** 依存先フィールドのコード */
  targetFieldCode: FieldCode;
  /** フォーム内のすべてのフィールド値（依存元の値を取得するため） */
  allValues?: FieldValue[];
}

/**
 * 動的変更セレクトフィールドのコンテナ層
 * 責務: ChangeSingleSelect専用のビジネスロジックとUI層の統合
 */
export const DynamicChangeSingleSelectFieldContainer = memo(
  ({
    fieldId,
    field,
    disabled,
    readOnly,
    className,
    sourceFieldCode,
    targetFieldCode,
    allValues = [],
    "data-testid": testId,
  }: DynamicChangeSingleSelectFieldContainerProps) => {
    const { options, uiState, hasSourceValueChanged } = useDynamicSelectField({
      fieldId,
      field,
      allValues,
      sourceFieldCode,
      targetFieldCode,
    });

    const { handleChange, value } = useFormField({
      fieldId,
      attribute: field,
    });

    // 依存元フィールドが変更されている場合は、値をundefinedに更新する
    if (value.currentValue && hasSourceValueChanged) {
      handleChange(undefined);
    }

    const renderInput = useCallback(
      ({
        value: inputValue,
        previousValue,
        onChange: handleChange,
        onFocus: handleFocus,
        onBlur: handleBlur,
        error: fieldError,
        placeholder,
        disabled: isDisabled,
        readOnly: isReadOnly,
        "data-testid": inputTestId,
      }: FieldRenderInputProps) => {
        const previousValueLabel = usePreviousValueLabel(
          previousValue,
          field,
          options,
        );

        if (!field) {
          return <div></div>;
        }

        const selectValue = extractValueFromValueLabel(inputValue) ?? "";

        const readOnlyDisplayValue = (() => {
          if (!inputValue) return "未登録";

          if (
            typeof inputValue === "object" &&
            inputValue !== null &&
            "label" in inputValue
          ) {
            return (inputValue as { label: string }).label;
          }

          return String(inputValue);
        })();

        const handleValueChange = (newValue: string) => {
          if (newValue && newValue !== "") {
            const selectedOption = options.find(
              (opt) => opt.value === newValue,
            );
            if (selectedOption?.label) {
              handleChange({
                value: newValue,
                label: selectedOption.label,
                ...(selectedOption.description && {
                  description: selectedOption.description,
                }),
              });
            } else {
              handleChange(newValue);
            }
          } else {
            handleChange(undefined);
          }
        };

        return (
          <DynamicChangeSelectFieldPresentation
            uiState={uiState}
            options={options}
            value={selectValue}
            onChange={handleValueChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={isDisabled}
            readOnly={isReadOnly}
            error={fieldError}
            placeholder={placeholder}
            testId={inputTestId}
            getSelectedOptionLabel={(value) =>
              getSelectedOptionLabel(value, options)
            }
            currentValueLabel={previousValueLabel}
            readOnlyDisplayValue={readOnlyDisplayValue}
          />
        );
      },
      [field, options, uiState],
    );

    return (
      <BaseField
        fieldId={fieldId}
        field={field}
        disabled={disabled}
        readOnly={readOnly}
        layout="vertical" // ChangeSingleSelect は縦並び固定
        className={className}
        data-testid={testId}
        renderInput={renderInput}
      />
    );
  },
);

DynamicChangeSingleSelectFieldContainer.displayName =
  "DynamicChangeSingleSelectFieldContainer";
